export interface Issue {
  path: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public issues: Issue[] = [],
    /** any extra fields the server put on `error` beyond code/message/issues (e.g. a duplicate-listing hint) */
    public data: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

async function handle<T>(res: Response): Promise<T> {
  // 204 No Content (every DELETE route) has no body — res.json() throws on it.
  if (res.ok) return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
  const j = (await res.json().catch(() => ({}))) as { error?: { code?: string; message?: string; issues?: Issue[] } & Record<string, unknown> };
  const data = { ...j.error };
  delete data.code;
  delete data.message;
  delete data.issues;
  throw new ApiError(
    res.status,
    j.error?.code ?? 'error',
    j.error?.message ?? (res.status === 429 ? 'Too many requests — please wait a bit' : 'Something went wrong'),
    j.error?.issues ?? [],
    data,
  );
}

const base = '/api/v1';

export const api = {
  get: <T>(path: string) => fetch(base + path).then(handle<T>),
  postForm: <T>(path: string, fd: FormData) => fetch(base + path, { method: 'POST', body: fd }).then(handle<T>),
  postJson: <T>(path: string, body?: unknown) =>
    fetch(base + path, {
      method: 'POST',
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      body: body === undefined ? null : JSON.stringify(body),
    }).then(handle<T>),
  putJson: <T>(path: string, body: unknown) =>
    fetch(base + path, { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }).then(
      handle<T>,
    ),
  patchJson: <T>(path: string, body: unknown) =>
    fetch(base + path, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }).then(
      handle<T>,
    ),
  delete: <T>(path: string) => fetch(base + path, { method: 'DELETE' }).then(handle<T>),
};

/** Fire-and-forget: analytics must never surface an error to the user or
 * block whatever UI action triggered it. Most named events (spec §18) are
 * recorded server-side at their natural route instead -- use this only for
 * events with no server-side hook, like a page view. */
export function track(eventName: string, properties?: Record<string, unknown>): void {
  api.postJson('/events/track', { event_name: eventName, properties }).catch(() => {});
}

export interface MapConfig {
  styleUrl: string;
  attribution: string;
  turnstileSiteKey: string | null;
}

let configPromise: Promise<MapConfig> | null = null;
export function getConfig(): Promise<MapConfig> {
  configPromise ??= api.get<MapConfig>('/map-config');
  return configPromise;
}

export interface GeocodeResult {
  lat: number;
  lng: number;
  label: string;
  kind: 'zip' | 'place' | 'address';
}

export interface EventProps {
  public_id: string;
  event_type: string;
  title: string;
  venue_name: string | null;
  address_1: string;
  city: string;
  state: string;
  postal_code: string;
  start_at: string;
  end_at: string;
  /** true when end_at is a scheduling placeholder, not a posted end time -- hoursPill/whenLong
   * omit the end clock time from display when this is set. */
  end_time_unknown: boolean;
  timezone: string;
  is_online_only: boolean;
  has_photos: boolean;
  thumb_url: string | null;
  distance_mi?: number;
  /** address hidden until address_reveal_at; lat/lng are an approximate area */
  approximate: boolean;
  address_reveal_at?: string;
  recurrence_text: string | null;
  recurrence_short: string | null;
}

export interface EventFeature {
  type: 'Feature';
  geometry: { type: 'Point'; coordinates: [number, number] } | null;
  properties: EventProps;
}

export interface EventDetail extends EventProps {
  description: string;
  address_2: string | null;
  external_url: string | null;
  special_instructions: string | null;
  status: string;
  lat: number;
  lng: number;
  photos: { url: string; width: number; height: number }[];
  upcoming: { start_at: string; end_at: string; end_time_unknown: boolean }[];
  /** present only for event_type 'auction' with any auction-specific detail filled in */
  auction: AuctionInfo | null;
}

export interface AuctionInfo {
  auctioneer_name: string | null;
  format: 'in_person' | 'online' | 'hybrid';
  auction_start_at: string | null;
  preview_start_at: string | null;
  preview_end_at: string | null;
  terms_url: string | null;
  catalog_url: string | null;
  pickup_info: string | null;
}

export interface ShopPlaceProps {
  public_id: string;
  name: string;
  category: string;
  address_1: string;
  city: string;
  state: string;
  postal_code: string;
  website_url: string | null;
  phone: string | null;
  kind: 'place';
}

export interface ShopPlaceFeature {
  type: 'Feature';
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: ShopPlaceProps;
}
